import { useEffect, useState } from 'react';
import { Cloud, Download, Plus, Trash2 } from 'lucide-react';
import { bridge } from './bridge';
import { uuid, type Entity, type NativeStatus } from './types';

type Rule = { id: string; field: 'app' | 'domain' | 'title'; pattern: string; category: 'work' | 'leisure' | 'other' };
type Props = {
  settings: Record<string, any>;
  status: NativeStatus | null;
  entities: Entity[];
  save: (type: Entity['type'], id: string, data: Record<string, unknown>, deleted?: boolean) => Promise<Entity>;
  refresh: () => Promise<void>;
};

const labels: Record<string, string> = {
  usage: '使用记录', overlay: '悬浮检查', notifications: '通知', accessibility: '前台应用识别', battery: '后台运行', startup: '开机启动',
};

const aiPresets = {
  deepseek: { baseUrl: 'https://api.deepseek.com', model: 'deepseek-chat' },
  qwen: { baseUrl: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1', model: 'qwen-plus' },
  gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai', model: 'gemini-2.5-flash' },
  custom: { baseUrl: '', model: '' },
};

export default function SettingsPanel({ settings, status, entities, save, refresh }: Props) {
  const connection = JSON.parse(localStorage.getItem('mainline.connection') || '{}');
  const [url, setUrl] = useState(connection.url || '');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [field, setField] = useState<Rule['field']>('app');
  const [pattern, setPattern] = useState('');
  const [category, setCategory] = useState<Rule['category']>('leisure');
  const [aiProvider, setAiProvider] = useState<keyof typeof aiPresets>('deepseek');
  const [aiBaseUrl, setAiBaseUrl] = useState(aiPresets.deepseek.baseUrl);
  const [aiModel, setAiModel] = useState(aiPresets.deepseek.model);
  const [aiKey, setAiKey] = useState('');
  const [aiHasKey, setAiHasKey] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const rules: Rule[] = Array.isArray(settings.rules) ? settings.rules : [];

  useEffect(() => {
    if (!status?.sync?.connected) return;
    bridge.api('/api/ai/config').then((config) => {
      const provider = Object.hasOwn(aiPresets, config.provider) ? config.provider as keyof typeof aiPresets : 'custom';
      setAiProvider(provider);
      setAiBaseUrl(config.baseUrl || aiPresets[provider].baseUrl);
      setAiModel(config.model || aiPresets[provider].model);
      setAiHasKey(Boolean(config.hasKey));
    }).catch((error) => setAiMessage(error instanceof Error ? error.message : '无法读取 AI 配置'));
  }, [status?.sync?.connected]);

  const update = (data: Record<string, unknown>) => save('settings', 'settings', { ...settings, ...data });

  const connect = async () => {
    try {
      const response = await fetch(`${url.replace(/\/$/, '')}/api/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error);
      localStorage.setItem('mainline.connection', JSON.stringify({ url, token: result.token }));
      await bridge.configure(url, result.token);
      setPassword('');
      setMessage('连接成功');
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '连接失败');
    }
  };

  const addRule = async () => {
    if (!pattern.trim()) return;
    await update({ rules: [...rules, { id: uuid(), field, pattern: pattern.trim(), category }] });
    setPattern('');
  };

  const removeRule = (id: string) => update({ rules: rules.filter((rule) => rule.id !== id) });

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), entities }, null, 2)], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = `mainline-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const chooseProvider = (provider: keyof typeof aiPresets) => {
    setAiProvider(provider);
    if (provider !== 'custom') {
      setAiBaseUrl(aiPresets[provider].baseUrl);
      setAiModel(aiPresets[provider].model);
    }
  };

  const saveAi = async () => {
    try {
      const config = await bridge.api('/api/ai/config', 'PUT', {
        provider: aiProvider, baseUrl: aiBaseUrl, model: aiModel, ...(aiKey.trim() ? { apiKey: aiKey.trim() } : {}),
      });
      setAiHasKey(Boolean(config.hasKey));
      setAiKey('');
      setAiMessage('AI 配置已安全保存到服务器');
    } catch (error) {
      setAiMessage(error instanceof Error ? error.message : '保存失败');
    }
  };

  return (
    <section className="settings-grid">
      <div className="panel">
        <span className="eyebrow">检查节奏</span>
        <h3>提醒与一天</h3>
        <label>固定检查间隔
          <select value={settings.intervalMinutes} onChange={(event) => update({ intervalMinutes: Number(event.target.value) })}>
            {[15, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{minutes} 分钟</option>)}
          </select>
        </label>
        <div className="row">
          <label>开始<input type="time" value={settings.startTime} onChange={(event) => update({ startTime: event.target.value })} /></label>
          <label>结束<input type="time" value={settings.endTime} onChange={(event) => update({ endTime: event.target.value })} /></label>
        </div>
        <label className="switch"><input type="checkbox" checked={settings.scheduledStartEnabled} onChange={(event) => update({ scheduledStartEnabled: event.target.checked })} /><span />按设定时间提醒开始一天</label>
        <label className="switch"><input type="checkbox" checked={settings.monitorEnabled} onChange={(event) => update({ monitorEnabled: event.target.checked })} /><span />后台记录与检查</label>
      </div>

      <div className="panel">
        <span className="eyebrow">识别规则</span>
        <h3>哪些活动算工作或娱乐</h3>
        <div className="rule-form">
          <select value={field} onChange={(event) => setField(event.target.value as Rule['field'])}>
            <option value="app">应用名称</option><option value="domain">网站域名</option><option value="title">窗口标题</option>
          </select>
          <input value={pattern} onChange={(event) => setPattern(event.target.value)} placeholder="例如：youtube.com" />
          <select value={category} onChange={(event) => setCategory(event.target.value as Rule['category'])}>
            <option value="work">工作</option><option value="leisure">娱乐</option><option value="other">其他</option>
          </select>
          <button className="quiet" onClick={addRule}><Plus size={16} />添加规则</button>
        </div>
        <div className="rule-list">
          {rules.map((rule) => (
            <div className="rule" key={rule.id}>
              <span>{rule.field === 'app' ? '应用' : rule.field === 'domain' ? '域名' : '标题'}</span>
              <b>{rule.pattern}</b>
              <em className={rule.category}>{rule.category === 'work' ? '工作' : rule.category === 'leisure' ? '娱乐' : '其他'}</em>
              <button onClick={() => removeRule(rule.id)} aria-label={`删除 ${rule.pattern}`}><Trash2 size={14} /></button>
            </div>
          ))}
          {!rules.length && <p className="privacy">尚未添加自定义规则。内置规则会识别 TikTok、YouTube、Facebook、Instagram、微博和哔哩哔哩。</p>}
        </div>
      </div>

      <div className="panel">
        <span className="eyebrow">跨设备同步</span>
        <h3>连接你的服务器</h3>
        <label>服务地址<input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://mainline.example.com" /></label>
        <label>个人密码<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="登录后不会保留明文密码" /></label>
        <button className="primary" onClick={connect}><Cloud />连接</button>
        {message && <p className="form-msg">{message}</p>}
      </div>

      <div className="panel">
        <span className="eyebrow">设备权限</span>
        <h3>{status?.platform === 'browser' ? '浏览器预览' : status?.platform === 'windows' ? 'Windows' : '安卓'}</h3>
        {Object.keys(labels).map((name) => (
          <button className="permission" key={name} onClick={() => bridge.permission(name)}>
            <span>{labels[name]}</span><b>{status?.permissions?.[name] ? '已就绪' : '去设置'}</b>
          </button>
        ))}
        <p className="privacy">只记录应用、网站域名与窗口标题；不采集按键、截图、完整网址或页面正文。</p>
      </div>

      {status?.platform === 'windows' && status.extensionPort && (
        <div className="panel">
          <span className="eyebrow">浏览器扩展</span>
          <h3>网站识别连接信息</h3>
          <p className="privacy">仅在本机浏览器扩展设置页中填写，不要发送给其他人。</p>
          <div className="credential"><span>端口</span><code>{status.extensionPort}</code></div>
          <div className="credential"><span>令牌</span><code>{status.extensionToken}</code></div>
        </div>
      )}

      <div className="panel">
        <span className="eyebrow">AI 与数据</span>
        <h3>Gemini · DeepSeek · Qwen</h3>
        <p>API 密钥在服务器端加密保存，设备之间不会同步密钥。</p>
        <label>服务商
          <select value={aiProvider} disabled={!status?.sync?.connected} onChange={(event) => chooseProvider(event.target.value as keyof typeof aiPresets)}>
            <option value="deepseek">DeepSeek</option><option value="qwen">Qwen</option><option value="gemini">Gemini</option><option value="custom">兼容接口</option>
          </select>
        </label>
        <label>接口地址<input value={aiBaseUrl} disabled={!status?.sync?.connected} onChange={(event) => setAiBaseUrl(event.target.value)} /></label>
        <label>模型<input value={aiModel} disabled={!status?.sync?.connected} onChange={(event) => setAiModel(event.target.value)} /></label>
        <label>API 密钥<input type="password" value={aiKey} disabled={!status?.sync?.connected} onChange={(event) => setAiKey(event.target.value)} placeholder={aiHasKey ? '已保存；留空表示不更换' : '连接服务器后输入'} /></label>
        <div className="panel-actions">
          <button className="quiet" disabled={!status?.sync?.connected || !aiBaseUrl || !aiModel} onClick={saveAi}>保存 AI 配置</button>
          <button className="quiet" onClick={exportData}><Download size={16} />导出全部记录</button>
        </div>
        {aiMessage && <p className="form-msg">{aiMessage}</p>}
      </div>
    </section>
  );
}
